import React from 'react';
import { ArrowRight, Image as ImageIcon, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { photoFilter } from '../../data/insightsConfig';

const serif = { fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 };

// Renders the cover photo, or a neutral placeholder when a post is published
// before its image is ready (e.g. Desi adds photos after publish). Fills the
// parent's aspect-ratio box in both cases.
export const CoverImage = ({ src, alt, imgClassName = '' }) => {
  if (!src) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-[#EDE3D5] text-[#8B7355]" data-testid="insight-cover-placeholder">
        <ImageIcon size={22} strokeWidth={1.5} />
        <span className="text-[11px] tracking-[0.12em] uppercase">Photo coming soon</span>
      </div>
    );
  }
  return <img src={src} alt={alt} loading="lazy" className={imgClassName} style={{ filter: photoFilter }} />;
};

export const CATEGORY_STYLES = {
  'White Paper': 'bg-[#2A2018] text-[#F5EDE1]',
  Research: 'bg-[#5B7B6A]/14 text-[#4C6A59]',
  Article: 'bg-[#EDE1CE] text-[#8B6D43]',
  'Case Study': 'bg-[#4A6B8A]/12 text-[#4A6B8A]',
  Announcement: 'bg-[#B8864B]/16 text-[#8B6030]',
};

export const CategoryBadge = ({ category, className = '' }) => (
  <span
    data-testid="insight-badge"
    className={`inline-flex items-center text-[10.5px] font-semibold tracking-[0.14em] uppercase px-3 py-1.5 rounded-full ${CATEGORY_STYLES[category] || 'bg-[#EDE1CE] text-[#8B6D43]'} ${className}`}
  >
    {category}
  </span>
);

const FORMAT_TAG_STYLE = 'bg-[#2A2018]/[0.06] text-[#5C4F42]';
const TOPIC_TAG_STYLE = 'bg-[#5B7B6A]/12 text-[#4C6A59]';

// Topic + format tag pills for a post. Plain badges by default; pass `onTagClick`
// (from the /insights index) to make each pill an interactive filter trigger.
export const TagPills = ({ pub, onTagClick, className = '' }) => {
  const hasTags = (pub.topicTags && pub.topicTags.length > 0) || pub.formatTag;
  if (!hasTags) return null;
  const Pill = ({ children, kind, value }) =>
    onTagClick ? (
      <button
        type="button"
        data-testid={`insight-tag-${kind}-${value.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onTagClick(kind, value); }}
        className={`text-[11px] font-medium px-2.5 py-1 rounded-full transition-colors ${kind === 'format' ? FORMAT_TAG_STYLE : TOPIC_TAG_STYLE} hover:opacity-75`}
      >
        {children}
      </button>
    ) : (
      <span className={`text-[11px] font-medium px-2.5 py-1 rounded-full ${kind === 'format' ? FORMAT_TAG_STYLE : TOPIC_TAG_STYLE}`}>
        {children}
      </span>
    );
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`} data-testid="insight-tag-pills">
      {(pub.topicTags || []).map((t) => <Pill key={t} kind="topic" value={t}>{t}</Pill>)}
      {pub.formatTag && <Pill kind="format" value={pub.formatTag}>{pub.formatTag}</Pill>}
    </div>
  );
};

// Large hero card for the pinned slot in the Insights "Editor's Picks" row (~60% width).
export const FeaturedHeroCard = ({ pub }) => (
  <Link
    to={`/insights/${pub.slug}`}
    data-testid="insight-featured-hero-card"
    className="group flex flex-col bg-[#FBF6EF] border border-[#2A2018]/[0.08] rounded-2xl overflow-hidden transition-all duration-300 hover:border-[#2A2018]/[0.18] hover:-translate-y-0.5 h-full"
  >
    <div className="relative aspect-[16/9] overflow-hidden bg-[#EDE3D5]">
      <CoverImage
        src={pub.coverImage}
        alt={pub.title}
        imgClassName="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]"
      />
      <div className="absolute top-5 left-5"><CategoryBadge category={pub.category} /></div>
    </div>
    <div className="flex flex-col flex-1 p-7 md:p-8">
      <h3 className="text-[#1F1812] leading-[1.1]" style={{ ...serif, fontWeight: 600, fontSize: 'clamp(1.7rem, 2.4vw, 2.15rem)' }}>{pub.title}</h3>
      {pub.author && (
        <p className="mt-3 text-[#7A5A2E] text-[13px]">By {pub.author}{pub.authorRole ? `, ${pub.authorRole}` : ''}</p>
      )}
      <TagPills pub={pub} className="mt-3" />
      <p className="mt-3 text-[#5C4F42] text-[15px] leading-[1.6] line-clamp-2">{pub.excerpt}</p>
      <div className="mt-6 pt-5 border-t border-[#2A2018]/[0.07] flex items-center justify-between">
        <span className="text-[#7A5A2E] text-[12.5px]">{pub.publishDate} · {pub.readingTime}</span>
        <span className="inline-flex items-center gap-1.5 text-[#1F1812] text-[13px] font-medium">
          Read Article<ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </div>
  </Link>
);

// Compact stacked card (image on top, text below) for the two dynamic "most-visited"
// slots alongside the hero pick.
export const FeaturedSideCard = ({ pub }) => (
  <Link
    to={`/insights/${pub.slug}`}
    data-testid="insight-featured-side-card"
    className="group flex flex-col bg-[#FBF6EF] border border-[#2A2018]/[0.08] rounded-2xl overflow-hidden transition-all duration-300 hover:border-[#2A2018]/[0.18] flex-1"
  >
    <div className="relative aspect-[16/9] overflow-hidden bg-[#EDE3D5]">
      <CoverImage
        src={pub.coverImage}
        alt={pub.title}
        imgClassName="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
      />
    </div>
    <div className="flex flex-col flex-1 p-4">
      <span className="inline-flex items-center gap-1 text-[#B8864B] text-[10px] tracking-[0.14em] uppercase font-semibold">
        <TrendingUp size={11} /> Most Read
      </span>
      <h4 className="mt-2 text-[#1F1812] leading-[1.2] line-clamp-2" style={{ ...serif, fontWeight: 600, fontSize: '21px' }}>{pub.title}</h4>
      <span className="mt-auto pt-2 text-[#7A5A2E] text-[11.5px]">{pub.publishDate} · {pub.readingTime}</span>
    </div>
  </Link>
);
// Editorial publication card used on the Insights grid and the homepage section.
export const InsightCard = ({ pub, onTagClick }) => {
  const cta = pub.category === 'White Paper' || pub.category === 'Research' ? 'Read Research' : 'Read Article';
  return (
    <Link
      to={`/insights/${pub.slug}`}
      data-testid="insight-card"
      className="group flex flex-col bg-[#FBF6EF] border border-[#2A2018]/[0.07] rounded-2xl overflow-hidden transition-all duration-300 hover:border-[#2A2018]/[0.16] hover:-translate-y-0.5"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-[#EDE3D5]">
        <CoverImage
          src={pub.coverImage}
          alt={pub.title}
          imgClassName="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
        />
        <div className="absolute top-4 left-4"><CategoryBadge category={pub.category} /></div>
      </div>
      <div className="flex flex-col flex-1 p-6">
        <h3 className="text-[#1F1812] text-[21px] leading-[1.15]" style={{ ...serif, fontWeight: 600 }}>{pub.title}</h3>
        <TagPills pub={pub} onTagClick={onTagClick} className="mt-3" />
        <p className="mt-3 text-[#5C4F42] text-[14px] leading-[1.6] line-clamp-2">{pub.excerpt}</p>
        <div className="mt-5 pt-4 border-t border-[#2A2018]/[0.07] flex items-center justify-between">
          <span className="text-[#7A5A2E] text-[12px]">{pub.publishDate} · {pub.readingTime}</span>
          <span className="inline-flex items-center gap-1.5 text-[#1F1812] text-[12.5px] font-medium">
            {cta}<ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </Link>
  );
};
